import { zodResolver } from '@hookform/resolvers/zod'
import {
	type ResidenceCreate,
	type ResidenceCreateResponse,
	residenceCreate
} from '@repo/sdk/gen'
import { zResidenceCreate, zResidenceManagerPerson } from '@repo/sdk/zod'
import { Button } from '@repo/ui/button'
import { DetailLayout } from '@repo/ui/components/detail-layout'
import { PageHeader } from '@repo/ui/components/page-header'
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
	FieldLegend,
	FieldSet
} from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { z } from 'zod'
import { type TKey, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { ManagerFields, managerEmailError } from '../-components/manager-fields'
import { residenceIssueMessage } from '../-lib/residence-form'

export const Route = createFileRoute('/(private)/residences/new/')({
	staticData: {
		breadcrumb: 'admin-residences:new.page_title'
	},
	component: NewResidence
})

// The API's own schemas: the name of the residence and the person who runs it.
const newResidenceSchema = zResidenceManagerPerson
	.pick({ firstName: true, lastName: true, email: true })
	.extend({ name: zResidenceCreate.shape.name })
type NewResidenceForm = z.infer<typeof newResidenceSchema>

/**
 * "Nuova struttura": the general admin gives its name and its facility
 * manager, who gets the invite and fills in everything else (decision D75).
 * One call creates both: a refused address creates nothing. The page address
 * is made from the name.
 */
function NewResidence() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const form = useForm<NewResidenceForm>({
		resolver: zodResolver(newResidenceSchema, {
			error: residenceIssueMessage
		}),
		defaultValues: { name: '', firstName: '', lastName: '', email: '' }
	})

	const create = useMutation({
		mutationFn: (data: ResidenceCreate) =>
			residenceCreate({ body: { data } }),
		onSuccess: async (response, { manager }) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.residences.all()
			})
			if (response.inviteSent) {
				toast.success(
					t(OUTCOME_MESSAGE[response.outcome], {
						email: manager.email
					})
				)
			} else {
				// Saved, but the email did not go out: resent from the managers of the residence.
				toast.error(t('admin-residences:new.invite_failed'))
			}
			await navigate({
				to: '/residences/$residenceId',
				params: { residenceId: response.data.id }
			})
		},
		onError: (error) => {
			const message = managerEmailError(error)
			if (message) {
				form.setError('email', { message: t(message) })
				return
			}
			toast.error(formatError(error))
		}
	})

	return (
		<DetailLayout
			header={<PageHeader title={t('admin-residences:new.page_title')} />}
		>
			<FormProvider {...form}>
				<form
					className='flex max-w-xl flex-col gap-8'
					noValidate
					onSubmit={form.handleSubmit(({ name, ...manager }) =>
						create.mutate({ name, manager })
					)}
				>
					<FieldSet>
						<FieldLegend>
							{t('admin-residences:new.residence_title')}
						</FieldLegend>
						<Controller
							control={form.control}
							name='name'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor='new-residence-name'>
										{t('admin-residences:fields.name')}
									</FieldLabel>
									<Input
										{...field}
										aria-invalid={fieldState.invalid}
										id='new-residence-name'
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
					</FieldSet>
					<FieldSet>
						<FieldLegend>
							{t('admin-residences:new.manager_title')}
						</FieldLegend>
						<FieldDescription>
							{t('admin-residences:new.manager_hint')}
						</FieldDescription>
						<ManagerFields />
					</FieldSet>
					<div>
						<Button disabled={create.isPending} type='submit'>
							{create.isPending && <Spinner />}
							{t('admin-residences:new.submit')}
						</Button>
					</div>
				</form>
			</FormProvider>
		</DetailLayout>
	)
}

const OUTCOME_MESSAGE = {
	invited: 'admin-residences:new.invited',
	linked: 'admin-residences:new.linked'
} as const satisfies Record<ResidenceCreateResponse['outcome'], TKey>
